import React, { useEffect, useState } from 'react';
import clsx from 'clsx';

import { useStyles } from './style';
import { Box, Chip, Collapse, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import StarIcon from '@material-ui/icons/Star';
import { ThemedButton } from '../../../../shared/components/themed-button';
import { useScreenSize } from '../../../../shared/hooks/use-screen-size';

// Define the structure of a feature object
interface Feature {
  name: string;
  free: boolean | string; // Can be boolean or string like 'Priority'
  unlimited: boolean | string;
  enterprise: boolean | string;
}

const features: Feature[] = [
  { name: 'Showcase Entire Product Range', free: true, unlimited: true, enterprise: true },
  { name: 'Unlimited Customers', free: true, unlimited: true, enterprise: true },
  { name: 'Unlimited Orders', free: true, unlimited: true, enterprise: true },
  { name: 'Automatic Order Reminders', free: true, unlimited: true, enterprise: true },
  { name: 'Purchase Orders & Pick Slips', free: true, unlimited: true, enterprise: true },
  { name: 'Customer Loss Reduction Tool', free: true, unlimited: true, enterprise: true },
  { name: 'Inventory Management', free: false, unlimited: true, enterprise: true },
  { name: 'Accounting Integrations', free: false, unlimited: true, enterprise: true },
  { name: 'Invoicing & Credit Notes', free: false, unlimited: true, enterprise: true },
  { name: 'Unlimited Price Lists', free: false, unlimited: true, enterprise: true },
  { name: 'Price Exceptions & Discounts', free: false, unlimited: true, enterprise: true },
  { name: 'Advanced Delivery Routing', free: false, unlimited: true, enterprise: true },
  { name: 'Team Management', free: false, unlimited: true, enterprise: true },
  { name: 'Personalised Ordering Mobile Apps', free: false, unlimited: false, enterprise: true },
  { name: 'Custom Branding & Domain (White-label)', free: false, unlimited: false, enterprise: true },
  { name: 'ERP Integrations', free: false, unlimited: false, enterprise: true },
  { name: 'Email/SMS/WhatsApp AI Digital Ordering', free: false, unlimited: false, enterprise: 'Coming Soon' },
  { name: 'Dedicated Support', free: false, unlimited: 'Priority', enterprise: 'Dedicated' },
];

type FeatureStatus = boolean | 'Priority' | 'Dedicated' | 'Coming Soon';
type PlanTypes = 'free' | 'unlimited' | 'enterprise' | 'pro_lite';
type CurrentPlan = PlanTypes | 'trial';

interface Props {
  currentStep: number;
  upgradePlan: () => void;
  currentPlan: CurrentPlan;
}

export const SupplierComparePlans: React.FC<Props> = ({ currentStep, upgradePlan, currentPlan }) => {
  const classes = useStyles();
  const { isMobile } = useScreenSize();
  // const [isComparePlansOpen, setIsComparePlansOpen] = useState<boolean>(currentPlan !== 'unlimited');
  const [isComparePlansOpen, setIsComparePlansOpen] = useState<boolean>(true);

  const toggleComparePlans = (): void => {
    setIsComparePlansOpen(!isComparePlansOpen);
  };

  const renderCellContent = (value: FeatureStatus): JSX.Element => {
    if (value === true) {
      return <CheckIcon className={classes.checkIcon} />;
    }
    if (value === false) {
      return <CloseIcon className={classes.closeIcon} />;
    }

    return <Typography variant="body2" className={classes.plainTextFeature}>{value}</Typography>;
  };

  // useEffect(() => {
  //   if(currentPlan === 'unlimited') {
  //     setIsComparePlansOpen(false);
  //   }
  // }, [currentPlan]);

  return <div className={clsx(classes.root)}>
      <Box>
        <Box onClick={toggleComparePlans} className={classes.compareTitleContainer}>
          <Typography className={classes.compareTitle}>
          Compare Plans
          </Typography>
          {isComparePlansOpen ? (
              <ExpandLessIcon className={classes.compareIcon} />
          ) : (
              <ExpandMoreIcon className={classes.compareIcon} />
          )}
        </Box>
        <Collapse in={isComparePlansOpen} className={classes.collapseContainer}>
          <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
            <Table>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableHeadCell} style={{textAlign: 'center', width: '49%'}}>Feature</TableCell>
                  <TableCell className={classes.tableHeadCell} style={{width: '17%'}}>Free</TableCell>
                  <TableCell className={clsx(classes.tableHeadCell, classes.unlimitedColumn)} style={{width: '17%'}}>
                    <Chip
                      icon={isMobile ? <></>: <StarIcon className={classes.recommendedChipIcon} fontSize='small' />}
                      label="RECOMMENDED"
                      className={classes.recommendedChipTable}
                    />
                    Pro Standard
                  </TableCell>
                  <TableCell className={classes.tableHeadCell} style={{width: '17%'}}>Enterprise</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {features.map((feature: Feature) => (
                  <TableRow key={feature.name} hover className={classes.tableRow}>
                    <TableCell component="th" scope="row" className={clsx(classes.featureCell, classes.tableCell)}>
                      {feature.name}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {renderCellContent(feature.free as FeatureStatus)}
                    </TableCell>
                    <TableCell className={clsx(classes.tableCell, classes.unlimitedColumn)}>
                      {renderCellContent(feature.unlimited as FeatureStatus)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {renderCellContent(feature.enterprise as FeatureStatus)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Box>
      {currentStep === 0 && (currentPlan === 'free' || currentPlan === 'trial') && <Box className={classes.keepFeaturesContainer}>
        <Typography variant="body2" className={classes.keepFeaturesText}>Upgrade Plan to Keep These Features</Typography>
        <ThemedButton
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            upgradePlan();
          }}
          title='Upgrade Plan'
          buttonStyle='blueDarkButton'
          width={300}
        />
      </Box>}
    </div>
};
